/**
 * Buff HUD Component - Shows active card buffs during gameplay
 * Displays buff icon, shield status, and visual effects
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Zap, Crown, Sparkles } from 'lucide-react';

const BuffHUD = ({ buffInfo, isGameActive }) => {
  if (!buffInfo || !isGameActive) return null;

  const {
    name,
    icon,
    color,
    cardName,
    cardRarity,
    shieldActive,
    shieldCount,
    shieldJustUsed,
    hasSlowMo,
    scoreMultiplier
  } = buffInfo;

  // Get rarity styling
  const getRarityGlow = () => {
    switch (cardRarity?.toLowerCase()) {
      case 'legendary':
        return 'shadow-yellow-500/50';
      case 'epic':
        return 'shadow-purple-500/50';
      case 'rare':
        return 'shadow-cyan-500/50';
      default:
        return 'shadow-gray-500/30';
    }
  };

  return (
    <motion.div
      className="absolute top-2 left-2 z-20"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      data-testid="buff-hud"
    >
      {/* Main buff indicator */}
      <motion.div
        className={`flex items-center gap-2 px-3 py-2 rounded-xl bg-black/80 border-2 shadow-lg ${getRarityGlow()}`}
        style={{ borderColor: color }}
        animate={shieldJustUsed ? { scale: [1, 1.2, 1] } : {}}
        transition={{ duration: 0.3 }}
      >
        {/* Buff icon */}
        <motion.div
          className="w-8 h-8 rounded-lg flex items-center justify-center text-xl"
          style={{ backgroundColor: `${color}30` }}
          animate={{ rotate: [0, 5, -5, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {icon}
        </motion.div>

        {/* Buff info */}
        <div className="flex flex-col">
          <span className="text-xs text-white/70 font-space uppercase tracking-wider">
            {cardName?.slice(0, 12)}
          </span>
          <span 
            className="text-sm font-anton"
            style={{ color }}
          >
            {name}
          </span>
        </div>

        {/* Shield indicator */}
        {shieldActive && shieldCount > 0 && (
          <AnimatePresence>
            <motion.div
              className="ml-2 flex items-center gap-1 px-2 py-1 rounded-full bg-cyan-500/20 border border-cyan-500/50"
              initial={{ scale: 0 }}
              animate={{ 
                scale: 1,
                boxShadow: shieldJustUsed 
                  ? ['0 0 0px rgba(0,255,255,0)', '0 0 20px rgba(0,255,255,0.8)', '0 0 0px rgba(0,255,255,0)']
                  : '0 0 10px rgba(0,255,255,0.3)'
              }}
              exit={{ scale: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Shield className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 font-bold text-sm">{shieldCount}</span>
            </motion.div>
          </AnimatePresence>
        )}

        {/* Shield used flash */}
        <AnimatePresence>
          {shieldJustUsed && (
            <motion.div
              className="absolute inset-0 rounded-xl bg-cyan-400/30 pointer-events-none"
              initial={{ opacity: 1 }}
              animate={{ opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            />
          )}
        </AnimatePresence>

        {/* Slow-mo indicator */}
        {hasSlowMo && (
          <motion.div
            className="ml-2 flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500/20 border border-yellow-500/50"
            animate={{ 
              boxShadow: ['0 0 5px rgba(255,215,0,0.3)', '0 0 15px rgba(255,215,0,0.6)', '0 0 5px rgba(255,215,0,0.3)']
            }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <Crown className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400 font-bold text-xs">SLOW</span>
          </motion.div>
        )}

        {/* Score multiplier badge */}
        {scoreMultiplier > 1 && (
          <motion.div
            className="ml-1 flex items-center gap-1 px-2 py-1 rounded-full"
            style={{ backgroundColor: `${color}20`, border: `1px solid ${color}50` }}
          >
            <Zap className="w-3 h-3" style={{ color }} />
            <span className="font-bold text-xs" style={{ color }}>
              x{scoreMultiplier.toFixed(1)}
            </span>
          </motion.div>
        )}
      </motion.div>

      {/* Visual effect particles for legendary */}
      {cardRarity?.toLowerCase() === 'legendary' && (
        <div className="absolute -inset-4 pointer-events-none overflow-hidden">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-yellow-400 rounded-full"
              style={{
                left: `${20 + Math.random() * 60}%`,
                top: `${20 + Math.random() * 60}%`
              }}
              animate={{
                y: [-10, 10, -10],
                opacity: [0.3, 1, 0.3],
                scale: [0.8, 1.2, 0.8]
              }}
              transition={{
                duration: 2 + Math.random(),
                repeat: Infinity,
                delay: i * 0.3
              }}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
};

// Shield Pop Effect - rendered on canvas when shield blocks a bomb
export const ShieldPopEffect = ({ x, y, onComplete }) => {
  return (
    <motion.div
      className="fixed pointer-events-none z-50"
      style={{ left: x, top: y, transform: 'translate(-50%, -50%)' }}
      initial={{ scale: 0, opacity: 1 }}
      animate={{ scale: 3, opacity: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      onAnimationComplete={onComplete}
    >
      <div className="relative">
        {/* Shield icon burst */}
        <Shield className="w-20 h-20 text-cyan-400" />
        
        {/* Ripple rings */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute inset-0 border-4 border-cyan-400 rounded-full"
            initial={{ scale: 1, opacity: 0.8 }}
            animate={{ scale: 2 + i, opacity: 0 }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
          />
        ))}

        {/* Sparkle particles */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={`spark-${i}`}
            className="absolute w-2 h-2 bg-cyan-300 rounded-full"
            style={{
              left: '50%',
              top: '50%'
            }}
            initial={{ x: 0, y: 0, opacity: 1 }}
            animate={{
              x: Math.cos((i / 8) * Math.PI * 2) * 80,
              y: Math.sin((i / 8) * Math.PI * 2) * 80,
              opacity: 0,
              scale: 0
            }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        ))}
      </div>
    </motion.div>
  );
};

// Multiplier Score Popup - shows when multiplier is applied
export const MultiplierPopup = ({ baseScore, multipliedScore, color, x, y }) => {
  return (
    <motion.div
      className="fixed pointer-events-none z-50 text-center"
      style={{ left: x, top: y, transform: 'translate(-50%, -50%)' }}
      initial={{ y: 0, opacity: 1, scale: 1 }}
      animate={{ y: -60, opacity: 0, scale: 1.5 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
    >
      <div className="flex flex-col items-center">
        <span className="text-white/60 text-sm line-through">+{baseScore}</span>
        <motion.span
          className="font-anton text-2xl"
          style={{ color, textShadow: `0 0 10px ${color}` }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 0.3 }}
        >
          +{multipliedScore}
        </motion.span>
        <Sparkles className="w-4 h-4" style={{ color }} />
      </div>
    </motion.div>
  );
};

export default BuffHUD;
